import { useMutation, useSuspenseQuery } from '@apollo/client';
import React from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { LanguageSelectorComboBox } from '@eluve/blocks';
import { Heading, Stack, Text, useToast } from '@eluve/components';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { PreferredDateFormatSetting } from './PreferredDateFormatSetting';
import {
  getDefaultTenantUserSettingsQuery,
  setDefaultTenantUserLanguageMutation,
} from './operations';

export interface AdminTenantUserDefaultSettingsPageProps {}

export const AdminTenantUserDefaultSettingsPage: React.FC<
  AdminTenantUserDefaultSettingsPageProps
> = () => {
  const toast = useToast();
  const tenantId = useTenantIdFromParams()!;

  const { data } = useSuspenseQuery(getDefaultTenantUserSettingsQuery, {
    variables: {
      tenantId,
    },
    context: eluveAdminHasuraContext,
  });

  const [setDefaultTenantUserLanguage] = useMutation(
    setDefaultTenantUserLanguageMutation,
    {
      onError: () => toast.error('Failed to update language'),
      optimisticResponse: (input) => ({
        updateDefaultTenantUserSettingsByPk: {
          __typename: 'DefaultTenantUserSettings' as const,
          tenantId,
          inputLanguage: input.inputLanguage,
          outputLanguage: input.outputLanguage,
          preferredDateFormat:
            data.tenantsByPk?.default_tenant_user_settings
              ?.preferredDateFormat ?? 'P',
        },
      }),
    },
  );

  const tenantDefaults = data.tenantsByPk?.default_tenant_user_settings;

  if (!tenantDefaults) {
    return (
      <span className="font-bold text-text-negative">
        This organization has no entry in default_tenant_user_settings.
        Something went wrong.
      </span>
    );
  }

  const inputLanguageCode = tenantDefaults.inputLanguage;
  const outputLanguagCode = tenantDefaults.outputLanguage;

  const handleLanguageUpdate = async (input: string, output: string) => {
    await setDefaultTenantUserLanguage({
      variables: {
        tenantId,
        inputLanguage: input,
        outputLanguage: output,
      },
      context: eluveAdminHasuraContext,
    });
  };

  return (
    <Stack gap={5}>
      <div>
        <Heading size="2xl" weight="semibold">
          Default Organization User Settings
        </Heading>
        <Text>
          When new users are added to this organization, these will be the
          default settings granted to them.
        </Text>
      </div>

      <Stack gap={2}>
        <Heading size="lg" weight="semibold">
          Spoken Language
        </Heading>
        <LanguageSelectorComboBox
          selectButtonStyles="w-full"
          selectedLanguageCode={inputLanguageCode}
          onSelectedLanguageCode={async (newCode) => {
            await handleLanguageUpdate(newCode, outputLanguagCode);
          }}
        />
      </Stack>
      <Stack gap={2}>
        <Heading size="lg" weight="semibold">
          Notes Language
        </Heading>
        <LanguageSelectorComboBox
          selectButtonStyles="w-full"
          selectedLanguageCode={outputLanguagCode}
          onSelectedLanguageCode={async (newCode) => {
            await handleLanguageUpdate(inputLanguageCode, newCode);
          }}
        />
      </Stack>
      <Stack gap={2}>
        <Heading size="lg" weight="semibold">
          Preferred Date Format
        </Heading>
        <PreferredDateFormatSetting />
      </Stack>
    </Stack>
  );
};
